<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Ideal Customer Profile</span>
    </template>

    <template #actions>
      <router-link :to="`/sales-plays/${playId}`" class="backLink">
        <AppIcon name="arrow_right" :size="14" class="backArrow" />
        Back to play
      </router-link>
      <button class="btn secondary" type="button" @click="reload" :disabled="saving || loading">
        Discard
      </button>
      <button
        class="btn primary"
        type="button"
        @click="save"
        :disabled="saving || loading || !dirty"
      >
        {{ saving ? 'Saving…' : 'Save changes' }}
      </button>
    </template>

    <div v-if="loading" class="pageLoading">Loading…</div>
    <div v-else-if="loadError" class="errorBanner">{{ loadError }}</div>

    <template v-else>
      <div v-if="saveError" class="errorBanner">{{ saveError }}</div>

      <header class="pageHead">
        <h1 class="title">ICP for {{ playName }}</h1>
        <p class="sub">
          Firmographic targeting for this play. The auto-approval gate scores candidates against
          these criteria before they reach the agent. Leave a field blank to skip it.
        </p>
        <p v-if="versionLabel" class="version">{{ versionLabel }}</p>
      </header>

      <section class="formCard">
        <div class="formField">
          <label class="formLabel" for="icp-industry">Industry</label>
          <input
            id="icp-industry"
            class="input"
            v-model="draft.industry"
            placeholder="e.g. B2B SaaS, Healthcare IT"
            maxlength="500"
          />
        </div>

        <div class="formField">
          <label class="formLabel" for="icp-geography">Geography</label>
          <input
            id="icp-geography"
            class="input"
            v-model="draft.geography"
            placeholder="e.g. North America, EMEA, US + Canada"
            maxlength="500"
          />
        </div>

        <div class="formField">
          <label class="formLabel" for="icp-funding">Funding stage</label>
          <input
            id="icp-funding"
            class="input"
            v-model="draft.funding_stage"
            placeholder="e.g. Series B–D, Public, PE-backed"
            maxlength="500"
          />
        </div>

        <div class="formField">
          <label class="formLabel" for="icp-revenue">Revenue</label>
          <input
            id="icp-revenue"
            class="input"
            v-model="draft.revenue"
            placeholder="e.g. $10M–$100M ARR"
            maxlength="500"
          />
        </div>

        <div class="formField">
          <label class="formLabel" for="icp-size">Company size</label>
          <input
            id="icp-size"
            class="input"
            v-model="draft.company_size"
            placeholder="e.g. 50–500 employees, 1k+"
            maxlength="500"
          />
        </div>
      </section>
    </template>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'
import { isFlockPlay } from '../composables/usePlayEngine'

// The five fields are the agreed-on play-level ICP schema. They mirror the
// chat tool (configure_sales_play's icp section) and the upsert service's
// accepted keys. Adding a sixth field here without also updating the chat
// tool would mean the chat could clobber it on the next edit — keep them
// in sync.
const FIELDS = ['industry', 'geography', 'funding_stage', 'revenue', 'company_size'] as const
type FieldKey = typeof FIELDS[number]

const route = useRoute()
const router = useRouter()
const { setScope, clearScope } = useChatScope()

const playId = computed(() => String(route.params.id || ''))

const playName = ref('')
const currentVersion = ref(0)
const draft = reactive<Record<FieldKey, string>>({
  industry: '',
  geography: '',
  funding_stage: '',
  revenue: '',
  company_size: '',
})
const baseline = ref('')
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')

function snapshot(): string {
  return JSON.stringify(FIELDS.map((k) => draft[k].trim()))
}

const dirty = computed(() => snapshot() !== baseline.value)

const versionLabel = computed(() =>
  currentVersion.value > 0 ? `Current version: v${currentVersion.value}` : '',
)

function applyConfig(config: Record<string, unknown> | null | undefined) {
  for (const k of FIELDS) {
    const raw = config?.[k]
    draft[k] = typeof raw === 'string' ? raw : ''
  }
  baseline.value = snapshot()
}

async function reload() {
  loading.value = true
  loadError.value = ''
  saveError.value = ''
  try {
    const resp = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}`)
    const detail = resp?.play || null
    // A Strategy play's ICP comes from its brief and is not edited here.
    if (isFlockPlay(detail)) {
      await router.replace(`/sales-plays/${playId.value}`)
      return
    }
    playName.value = detail?.name || ''
    currentVersion.value = Number(detail?.icp?.current_version || 0)
    applyConfig(detail?.icp?.config as Record<string, unknown> | null | undefined)
  } catch (e: any) {
    loadError.value = e?.message || 'Failed to load ICP'
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  saveError.value = ''
  try {
    // Strip blanks: leaving a value out of `config` means "not set" — we
    // don't persist empty strings since downstream consumers (auto-approve,
    // dismissal learner) ignore them anyway and they clutter the rendered
    // ICP card on the detail page.
    const config: Record<string, string> = {}
    for (const k of FIELDS) {
      const v = draft[k].trim()
      if (v) config[k] = v
    }
    const resp = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}/icp`, {
      method: 'PUT',
      body: { config },
    })
    const icp = resp?.icp || null
    currentVersion.value = Number(icp?.current_version || 0)
    applyConfig(icp?.config as Record<string, unknown> | null | undefined)
  } catch (e: any) {
    saveError.value = e?.message || 'Save failed'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  setScope({
    scopeType: 'sales_play',
    scopeSubjectId: playId.value,
    pageContext: { sales_play_id: playId.value, page_type: 'sales_play_icp' },
  })
  await reload()
})

onBeforeUnmount(() => {
  clearScope()
})
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: var(--lh-tight);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-accent); }
.backArrow { transform: rotate(180deg); }

.pageLoading { color: var(--color-text-tertiary); font-size: var(--fs-sm); }

.errorBanner {
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-4);
  background: var(--color-danger-soft);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);
  font-size: var(--fs-sm);
  color: var(--color-danger);
}

.pageHead {
  max-width: 720px;
  margin-bottom: var(--space-5);
}
.title {
  margin: 0 0 var(--space-2);
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
}
.sub {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
}
.version {
  margin: var(--space-2) 0 0;
  font-family: var(--font-mono);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}

.formCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-width: 720px;
  padding: var(--space-4);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-neu-out-cream-sm);
}

.formField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.formLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}
</style>
